import { useEffect, useEffectEvent, useRef } from "react";
import { useStreamReveal } from "../../hooks/useStreamReveal";
import { Markdown } from "../Markdown";
import { Text } from "../Text";
import { MarkdownStyles } from "./MarkdownStyles";

type Props = {
  text: string;
  /** True while the entry is being actively streamed via SSE. */
  streaming?: boolean;
  onAnimatingChange?: (animating: boolean) => void;
  /** Render plain text instead of Markdown. */
  plain?: boolean;
  variant?: "text-regular" | "text-small";
  color?: string;
  citationEntryId?: string;
  resolveFileHref?: (href: string) => string | null;
};

export function TypewriterText({
  text,
  streaming,
  onAnimatingChange,
  plain,
  variant = "text-regular",
  color,
  resolveFileHref,
  citationEntryId,
}: Props) {
  const { revealed, animating } = useStreamReveal(text, streaming);

  const reportAnimating = useEffectEvent((value: boolean) =>
    onAnimatingChange?.(value)
  );

  // Rows share one callback, so only report transitions this row caused —
  // otherwise a row mounting or unmounting clears the flag for a row that is
  // still animating.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (animating === reportedRef.current) return;
    reportedRef.current = animating;
    reportAnimating(animating);
  }, [animating]);

  useEffect(
    () => () => {
      if (reportedRef.current) reportAnimating(false);
    },
    []
  );

  return (
    <Text variant={variant} style={{ minWidth: 0 }} color={color}>
      {plain ? (
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {revealed}
        </span>
      ) : (
        <MarkdownStyles
          variant={variant}
          lineHeight={variant === "text-small" ? "16px" : "20px"}
          bulletSpacing="8px"
        >
          <Markdown
            internalHref={resolveFileHref}
            citationEntryId={citationEntryId}
            tableControls
          >
            {revealed}
          </Markdown>
        </MarkdownStyles>
      )}
    </Text>
  );
}
